import React from 'react';

import './PortableBasicStats.css';

import { useDispatch, useSelector } from 'react-redux';

import { selectGeneralStats, selectPlayerToShowStats } from '../../selectors';

import Text from '../../../../components/Text/Text';

import Flex from '../../../../components/Flex/Flex';

import formatHands from '../../../../utils/formatHands';

import formatWinnings from '../../../../utils/formatWinnings';

import { Tag } from 'antd';

import { getPlayerStatsHref, getPortablePlayerBadgeType, pokerSitePostfix } from '../../utils';

import { findStats } from '../../actions';

import FishBadge from '../../../../components/FishBadge/FishBadge';

import RegBadge from '../../../../components/RegBadge/RegBadge';

import PhoneBadge from '../../../../components/PhoneBadge/PhoneBadge';

const { CheckableTag } = Tag;

function shouldUseNativeLink(event: React.MouseEvent<HTMLAnchorElement>) {
  return event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
}

const PortableBasicStats = React.memo(() => {
  const dispatch = useDispatch();

  const playerToShowStats = useSelector(selectPlayerToShowStats);

  const generalStats = useSelector(selectGeneralStats);

  const portablePlayerBadgeType = getPortablePlayerBadgeType(
    generalStats?.hands,

    generalStats?.bb100,

    generalStats?.vpip,

    generalStats?.pfr,
  );

  const winningsType =
    generalStats.winnings == null
      ? undefined
      : generalStats.winnings > 0
        ? ('success' as const)
        : ('error' as const);

  const handleSearchByPokersite = React.useCallback(
    (playername: string, pokersiteId: 1 | 2 | 3 | 4 | 5 | 6) => {
      dispatch(
        findStats({
          playername,

          pokersiteId,
        }),
      );
    },

    [dispatch],
  );

  const handleSameNickPlayerClick = React.useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement>,

      playername: string,

      pokersiteId: 1 | 2 | 3 | 4 | 5 | 6,
    ) => {
      if (shouldUseNativeLink(event)) {
        return;
      }

      event.preventDefault();

      handleSearchByPokersite(playername, pokersiteId);
    },

    [handleSearchByPokersite],
  );

  return (
    <Flex className="PortableBasicStats" align="center" justify="space-between" gap={5}>
      <Flex vertical gap={2} className="PortableBasicStats__PlayerInfo">
        <Flex align="center" gap={5} className="PortableBasicStats__HeaderMain">
          <Flex align="center" gap={4} className="PortableBasicStats__Identity">
            <Text size={3} bold className="PortableBasicStats__Name">
              {playerToShowStats?.playername}
            </Text>

            {portablePlayerBadgeType === 'fish' && <FishBadge vpip={generalStats?.vpip} />}

            {portablePlayerBadgeType === 'reg' && (
              <RegBadge
                hands={generalStats?.hands}
                bb100={generalStats?.bb100}
                vpip={generalStats?.vpip}
                pfr={generalStats?.pfr}
              />
            )}

            <PhoneBadge hands={generalStats?.hands} wtsd={generalStats?.sawshowdown} />
          </Flex>

          <Text size={3} bold className="PortableBasicStats__VpipPfr">
            {`${generalStats.vpip ?? '-'}/${generalStats.pfr ?? '-'}`}
          </Text>
        </Flex>

        {playerToShowStats && playerToShowStats.sameNickPlayers.length > 0 && (
          <Flex gap={6} className="PortableBasicStats__SameNickPlayers">
            {playerToShowStats.sameNickPlayers.map((player) => (
              <a
                key={player.id}
                className="PortableBasicStats__SameNickPlayerLink"
                href={getPlayerStatsHref(
                  player.playername,

                  player.pokersite_id,
                )}
                onClick={(event) =>
                  handleSameNickPlayerClick(
                    event,

                    player.playername,

                    player.pokersite_id,
                  )
                }
              >
                <CheckableTag checked className="PortableBasicStats__SameNickPlayer">
                  {pokerSitePostfix[player.pokersite_id]}
                </CheckableTag>
              </a>
            ))}
          </Flex>
        )}
      </Flex>

      <Flex className="PortableBasicStats__Metrics" align="center" gap={5}>
        <Text size={2} bold className="PortableBasicStats__Hands">
          {generalStats.hands == null ? '-' : formatHands(generalStats.hands)}
        </Text>

        <span className="PortableBasicStats__MetricDivider" />

        <Flex align="center" gap={4}>
          <Text size={2} type="secondary">
            W
          </Text>

          <Text size={2} bold type={winningsType}>
            {generalStats.winnings == null ? '-' : formatWinnings(generalStats.winnings, 1, true)}
          </Text>
        </Flex>

        <span className="PortableBasicStats__MetricDivider" />

        <Flex align="center" gap={4}>
          <Text size={2} type="secondary">
            WR
          </Text>

          <Text size={2} bold type={winningsType}>
            {generalStats.bb100 ?? '-'}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
});

export default PortableBasicStats;
